<template>
  <!-- TLDR:
  this component, when enough elements on the screen can translate/scroll, will create two segments based
  on what is in the slot. those two segments are animated to translate between the beginning
  of segment-1 to the beginning of segment-2. once it reaches the beginning of segment-2, it resets the animation.

  if at any point in the middle of the animation it's hovering/scrolling/grabbing/touching,
  we want to grab the last known position of segment-1, directly bind the position to the event,
  and translate/scroll the segments to the desired position. after the event finishes, translate/reset
  to position 0, reset the animation to start, and continue to "infinite scroll"

  if for some reason you find a bug, or need to update this component, feel free to reach out :)

  Cheers!
  Jesse Garcia
  hello@jesseg.co
  -->
  <div id="banner-container" ref="bannerContainer" :style="cssVars.style">
    <div
      v-if="debug"
      id="debug-container"
      class="d-flex flex-column position-absolute font-weight-bold"
      style="z-index: 100; color: red; opacity: 0.5; width: 10rem"
    >
      <div>Position: {{ Math.floor(autoScroll.position) }} / {{ autoScroll.max }}</div>
      <div>Auto Scrolling: {{ running }}</div>
      <div>Auto State: {{ autoScroll.state }}</div>
      <div>Cycle: {{ Math.floor(cycle / 60) }} seconds</div>
      <button @click="running = !running">Toggle Auto Scroll</button>
    </div>
    <div v-if="!scrollable" ref="noScroll" class="segment">
      <slot />
    </div>
    <div
      v-else
      id="scroll-container"
      ref="scrollContainer"
      @mouseenter="mouseEventHandler"
      @mouseleave="mouseEventHandler"
      @mousewheel="mouseEventHandler"
      @mousedown="mouseEventHandler"
      @mousemove="mouseEventHandler"
      @mouseup="mouseEventHandler"
      @touchstart="touchEventHander"
      @touchend="touchEventHander"
      @touchmove="touchEventHander"
    >
      <div id="first" ref="segment1" :class="segmentClass">
        <slot />
      </div>
      <div id="second" :class="segmentClass">
        <slot />
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    featuredPageCount: { type: Number, default: 5 },
    speed: { type: Number, default: 1 },
    debug: { type: Boolean, default: false },
  },
  data() {
    return {
      running: false,
      scrollable: false,
      bannerInterval: null,
      resize: {
        timeout: undefined,
        initialHandlers: undefined,
        handlers: undefined,
        observer: undefined,
      },
      autoScroll: {
        position: 0,
        max: undefined,
        duration: undefined,
        state: "start",
        timeout: undefined,
        offset: undefined,
      },
      grabbing: {
        initX: undefined,
        startX: undefined,
      },
      hovering: false,
    };
  },
  computed: {
    cycle() {
      // Update slot length check for Vue 3
      const slotContent = this.$slots.default?.();
      return (100000 * (slotContent?.length ?? 0)) / this.speed;
    },
    segmentClass() {
      // * generates the classes based on changing properties in the component
      const classes = ["segment"];
      if (this.debug) classes.push("debug");
      classes.push(`scroll-auto-${this.autoScroll.state}`);
      return classes.join(" ");
    },
    cssVars() {
      // * generates css variables based on changing properties in the component
      return {
        style: {
          "--play-state": this.running ? "running" : "paused",
          "--width": `${100 / this.featuredPageCount}vw`,
          "--duration": this.autoScroll.duration && `${this.autoScroll.duration}ms`,
          "--max-width": this.autoScroll.max && `-${this.autoScroll.max}px`,
          "--position": `-${this.autoScroll.position}px`,
        },
      };
    },
    scrollingParent() {
      // * tldr: walks up the dom to find a scrolling element. While on it's search,
      // * if it reaches the body tag, it will stop it's search and return null.
      const getScrollingParent = (element = this.$el) => {
        const isScrolling = element.clientHeight < element.scrollHeight;
        if (isScrolling) return element;
        if (element.tagName !== document.body.tagName) {
          return getScrollingParent(element.parentNode);
        }
        return null;
      };
      return getScrollingParent();
    },
  },
  watch: {
    running(running) {
      // * when the running state changes in the component
      const boundRec = this.$refs.segment1?.getBoundingClientRect();
      if (this.scrollable && boundRec) {
        this.setAutoScrollPosition(-1 * (boundRec.x - this.autoScroll.offset));

        if (this.getRealativePosition(boundRec.x) > 0) {
          if (running) this.triggerResetAnimation();
          else this.autoScroll.state = "stop";
        }
      }
    },
    hovering(hovering) {
      // * when the hoving state changes in the component
      if (!hovering) this.grabHandler("end", {}, 0);
    },
    "resize.handlers": function (handlers) {
      this.resizeHandler([...this.resize.initialHandlers, ...handlers]);
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.setDuration();

      this.resize.initialHandlers = [
        {
          ref: this.$refs.bannerContainer,
          callback: this.onBannerContainerResize,
        },
      ];

      this.resizeHandler();
    });
  },
  beforeUnmount() {
    const { observer } = this.resize;
    if (observer) observer.disconnect();
    if (this.bannerInterval) {
      clearInterval(this.bannerInterval);
    }
  },
  methods: {
    resizeHandler(handlers = this.resize.initialHandlers) {
      if (this.resize.observer) this.resize.observer.disconnect();
      this.resize.observer = new ResizeObserver((entries) =>
        entries.forEach((entry) => {
          handlers.forEach((handler) => {
            if (handler.ref.id === entry.target.id) handler.callback(entry);
          });
        }),
      );

      handlers.forEach((handler) => this.resize.observer.observe(handler.ref));
      /* if (handlers.length > 0) {
        handlers.forEach((handler) => (handler.ref == Element ? this.resize.observer.observe(handler.ref) : ''));
      } */
    },
    onSegment1Resize(entry) {
      this.autoScroll.max = Math.floor(entry.contentRect.width);
    },
    setDuration() {
      let count = 0;
      this.bannerInterval = setInterval(() => {
        if (count > 5) clearInterval(this.bannerInterval);
        const slotContent = this.$slots.default?.();
        if (slotContent && slotContent.length > 1) {
          this.autoScroll.duration = this.cycle;
          clearInterval(this.bannerInterval);
        }
        count += 1;
      }, 100);
    },
    onBannerContainerResize(entry) {
      const { contentRect } = entry;
      clearTimeout(this.resize.timeout);
      this.running = false;
      this.resize.timeout = setTimeout(() => {
        const scrollingParentWidth = this.scrollingParent?.clientWidth ?? 0;
        this.autoScroll.offset = scrollingParentWidth - contentRect.width;

        if (this.$refs.noScroll) {
          const { offsetWidth } = this.$refs.noScroll;
          if (offsetWidth > contentRect.width) {
            this.scrollable = true;
            this.$nextTick(() => {
              this.resize.handlers = [
                {
                  ref: this.$refs.segment1,
                  callback: this.onSegment1Resize,
                },
              ];
            });
          }
        } else if (this.scrollable && this.autoScroll.max) {
          if (this.autoScroll.max <= contentRect.width) {
            this.scrollable = false;
            this.autoScroll = Object.assign(this.autoScroll, {
              position: 0,
              max: undefined,
            });
            this.resize.observer.disconnect();
          }
        } else if (this.$slots.default?.().length > this.featuredPageCount) {
          this.scrollable = true;
          this.$nextTick(() => {
            this.resize.handlers = [
              {
                ref: this.$refs.segment1,
                callback: this.onSegment1Resize,
              },
            ];
          });
        }
        this.running = !this.debug;
      }, 100);
    },
    triggerResetAnimation({ max, position } = this.autoScroll) {
      const duration = Math.floor(this.cycle * ((max - position) / max));
      this.autoScroll.duration = duration;
      this.autoScroll.state = "reset";

      clearInterval(this.autoScroll.timeout);

      // as soon as the reset animation has finished, go ahead and reset to start animation
      this.autoScroll.timeout = setTimeout(() => {
        this.autoScroll = Object.assign(this.autoScroll, {
          position: 0,
          duration: this.cycle,
          state: "start",
        });
      }, duration - 1);
    },
    getRealativePosition(value, max = this.autoScroll.max) {
      // * calculates the realtive position based on the autoScroll.max
      // ? ie: I scroll 700, the max is 500. therefore I scroll 500, reset to 0, scroll 200 more.
      // ? why this way? because it simplifies some of the logic in the component where called.
      if (value < 0) return value + max;
      if (value > max) return value - max;
      return value;
    },
    setAutoScrollPosition(value) {
      // * sets the position of the autoScroll data object based on the realative position.
      // ? why create this method? so I can not worry about getting the realative position.
      this.autoScroll.position = this.getRealativePosition(value);
    },
    grabHandler(stage, grabbing = {}, timeout = 1000) {
      // * logic for how to handle a "grab" event
      const grab = Object.assign(this.grabbing, {
        moveX: undefined,
        ...grabbing,
      });

      if (stage === "start") {
        this.running = false;
        this.grabbing = grabbing;
      }
      if (stage === "move") {
        this.setAutoScrollPosition(grab.initX + (grab.startX - grab.moveX));
      }
      if (stage === "end") {
        this.grabbing = { initX: undefined, startX: undefined };
        setTimeout(() => {
          const { hovering, debug } = this;
          if (!debug && !hovering) this.running = true;
        }, timeout);
      }
    },
    mouseEventHandler(event) {
      // * handler for when a mouse event is triggered in the scrollableContainer
      const { type, clientX } = event;
      if (type.includes("mouseenter") && !this.debug) {
        this.running = false;
        this.hovering = true;
      }
      if (type.includes("mouseleave") && !this.debug) {
        this.running = true;
        this.hovering = false;
      }
      if (type.includes("mousewheel")) this.setAutoScrollPosition(this.autoScroll.position + event.deltaY, true);
      if (type.includes("mousedown")) {
        this.grabHandler("start", {
          initX: this.autoScroll.position,
          startX: clientX,
        });
      }
      if (this.grabbing.startX && type.includes("mousemove")) {
        this.grabHandler("move", {
          moveX: clientX,
        });
      }
      if (type.includes("mouseup")) {
        this.grabHandler("end", {}, 0);
      }
    },
    touchEventHander(event) {
      // * handler for when a touch event is triggered in the scrollableContainer
      const { type, touches } = event;
      if (type.includes("touchstart")) {
        this.grabHandler("start", {
          initX: this.autoScroll.position,
          startX: touches[0].clientX,
        });
      }
      if (type.includes("touchmove")) {
        this.grabHandler("move", {
          moveX: touches[0].clientX,
        });
      }
      if (type.includes("touchend")) {
        this.grabHandler("end");
      }
    },
  },
};
</script>

<style lang="scss" scoped>
#banner-container {
  display: flex;
  position: relative;
  overflow-x: hidden;
  width: 100%;
  height: 100%;
  user-select: none;

  @keyframes autoStartScroll {
    from {
      transform: translateX(0px);
    }
    to {
      transform: translateX(var(--max-width));
    }
  }

  @keyframes autoResetScroll {
    from {
      transform: translateX(var(--position));
    }
    to {
      transform: translateX(var(--max-width));
    }
  }

  .segment {
    display: flex;

    &.scroll-auto-start {
      animation-name: autoStartScroll;
      animation-play-state: var(--play-state);
      animation-duration: var(--duration);
      animation-iteration-count: infinite;
      animation-timing-function: linear;
    }

    &.scroll-auto-reset {
      animation-name: autoResetScroll;
      animation-play-state: var(--play-state);
      animation-duration: var(--duration);
      animation-iteration-count: infinite;
      animation-timing-function: linear;
    }

    &.scroll-auto-stop {
      transform: translateX(var(--position));
    }

    & > * {
      min-width: var(--width);
    }

    &#first.debug {
      background: green;
    }

    &#second.debug {
      background: blue;
    }
  }

  #scroll-container {
    display: flex;
    overflow-x: hidden;
    cursor: grab;
    overscroll-behavior-y: contain;

    &:active {
      cursor: grabbing;
    }
  }
}
</style>
